import React, { useCallback, useEffect, useState } from 'react';
import {
    fetchClientDebugAnalytics,
    fetchClientDebugJobs,
    fetchClientDebugPlaybooks,
} from '../api/restapi';
import '../assets/AllTabsCommon.css';
import '../assets/ClientDebugAnalytics.css';

const STATUS_LABELS = {
    WAITING_FOR_LOGS: 'Waiting for logs',
    QUEUED: 'Queued',
    RUNNING: 'Running',
    COMPLETED: 'Completed',
    PARTIAL: 'Partial',
    NOT_DEBUGGABLE: 'Not debuggable',
    FAILED: 'Failed',
};

const KPI_RANGES = [7, 30, 90];

function statusLabel(status) {
    return STATUS_LABELS[status] || status || 'Unknown';
}

function formatTimestamp(value) {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatDuration(ms) {
    if (ms == null || ms === '') return '—';
    const value = Number(ms);
    if (!Number.isFinite(value)) return '—';
    if (value < 1000) return `${Math.round(value)} ms`;
    return `${(value / 1000).toFixed(1)} s`;
}

function formatNumber(value) {
    if (value == null || value === '') return '—';
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString() : '—';
}

function formatCurrency(value) {
    if (value == null || value === '') return '—';
    const number = Number(value);
    return Number.isFinite(number) ? `$${number.toFixed(2)}` : '—';
}

function asObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asList(value) {
    return Array.isArray(value) ? value : [];
}

function displayList(value) {
    return asList(value).map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.type || item.name || '—';
        return String(item);
    }).join(', ') || '—';
}

function safeLogicalLocation(value) {
    if (typeof value !== 'string' || !value.startsWith('s3://') || value.includes('?') || value.includes('#')) {
        return 'Unavailable';
    }
    return value;
}

function formatWindowValue(value) {
    if (value == null || value === '') return null;
    return String(value).replace(/^PT/, '').toLowerCase();
}

function collectionWindow(playbook) {
    const window = playbook.collectionWindow || playbook.logWindow;
    if (typeof window === 'string') return window;
    if (window && typeof window === 'object') {
        const before = formatWindowValue(window.before);
        const after = formatWindowValue(window.after);
        if (before || after) return `${before || '—'} before · ${after || '—'} after`;
    }
    if (playbook.collectionWindowMinutes && typeof playbook.collectionWindowMinutes === 'object') {
        const before = formatWindowValue(playbook.collectionWindowMinutes.before);
        const after = formatWindowValue(playbook.collectionWindowMinutes.after);
        if (before || after) return `${before || '—'} minutes before · ${after || '—'} minutes after`;
    }
    return 'Not specified';
}

function artifactSummary(artifacts) {
    const values = asList(artifacts);
    const logs = values.filter((artifact) => artifact.kind === 'log').length;
    const files = values.filter((artifact) => artifact.kind === 'file').length;
    return `${logs} log${logs === 1 ? '' : 's'}${files ? ` · ${files} upload${files === 1 ? '' : 's'}` : ''}`;
}

function MetricCard({ label, value, className = '' }) {
    return (
        <div className={`client-debug-metric-card${className ? ` ${className}` : ''}`}>
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

function AnalyticsSummary({ analytics, loading, error, rangeDays, onRangeChange }) {
    const statusCounts = asObject(analytics.statusCounts);
    const duration = asObject(analytics.duration);
    const usage = asObject(analytics.usage);
    const usageTotal = asObject(usage.total);
    const logs = asObject(analytics.logs);
    const devices = asObject(analytics.devices);
    const email = asObject(analytics.email);
    const stageDurations = asObject(analytics.stageDurations);
    const playbookUsage = asObject(analytics.playbookUsage);

    return (
        <section className="client-debug-summary" aria-label="Client Debug team KPIs">
            <div className="client-debug-section-heading">
                <div>
                    <h2>Team KPIs</h2>
                    <p>Metadata-only aggregates for all Analytics users.</p>
                </div>
                <div className="client-debug-range-controls" role="group" aria-label="KPI range">
                    {KPI_RANGES.map((range) => (
                        <button
                            type="button"
                            key={range}
                            className={`client-debug-range-button${rangeDays === range ? ' active' : ''}`}
                            aria-pressed={rangeDays === range}
                            onClick={() => onRangeChange(range)}
                        >
                            {range} days
                        </button>
                    ))}
                </div>
            </div>

            {error && <div className="client-debug-load-error">{error}</div>}
            {loading ? <div className="client-debug-summary-loading">Loading KPI summary…</div> : (
                <>
                    <div className="client-debug-metric-grid">
                        <MetricCard label="Total jobs" value={formatNumber(analytics.totalJobs)} />
                        <MetricCard label="Completed" value={formatNumber(statusCounts.COMPLETED ?? analytics.completedCount)} className="success" />
                        <MetricCard label="Partial" value={formatNumber(statusCounts.PARTIAL ?? analytics.partialCount)} className="warning" />
                        <MetricCard label="Not debuggable" value={formatNumber(statusCounts.NOT_DEBUGGABLE ?? analytics.notDebuggableCount)} className="warning" />
                        <MetricCard label="Failed" value={formatNumber(statusCounts.FAILED ?? analytics.failedCount)} className="danger" />
                        <MetricCard label="No logs found" value={formatNumber(analytics.noLogCount)} className="warning" />
                        <MetricCard label="Unique devices" value={formatNumber(devices.unique)} />
                    </div>

                    <div className="client-debug-summary-grid">
                        <div className="client-debug-summary-card">
                            <h3>End-to-end duration</h3>
                            <div className="client-debug-summary-values">
                                <div><span>Average</span><strong>{formatDuration(duration.averageMs)}</strong></div>
                                <div><span>Median</span><strong>{formatDuration(duration.medianMs)}</strong></div>
                                <div><span>P95</span><strong>{formatDuration(duration.p95Ms)}</strong></div>
                            </div>
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Evidence and email</h3>
                            <div className="client-debug-summary-values">
                                <div><span>Logs staged</span><strong>{formatNumber(logs.pickedUp)}</strong></div>
                                <div><span>Logs analyzed</span><strong>{formatNumber(logs.analyzed)}</strong></div>
                                <div><span>Email sent / failed</span><strong>{formatNumber(email.sent)} / {formatNumber(email.failed)}</strong></div>
                            </div>
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Usage</h3>
                            <div className="client-debug-summary-values">
                                <div><span>LLM calls</span><strong>{formatNumber(usageTotal.llmCalls)}</strong></div>
                                <div><span>Input tokens</span><strong>{formatNumber(usageTotal.inputTokens)}</strong></div>
                                <div><span>Output tokens</span><strong>{formatNumber(usageTotal.outputTokens)}</strong></div>
                                <div><span>Cost</span><strong>{formatCurrency(usageTotal.costUsd)}</strong></div>
                            </div>
                        </div>
                    </div>

                    <div className="client-debug-summary-grid">
                        <div className="client-debug-summary-card">
                            <h3>Stage durations</h3>
                            {Object.keys(stageDurations).length === 0 ? <p className="client-debug-muted">No stage duration data.</p> : (
                                <div className="client-debug-stage-metrics">
                                    {Object.entries(stageDurations).map(([stage, metrics]) => (
                                        <div key={stage}>
                                            <strong>{stage}</strong>
                                            <span>Average {formatDuration(metrics?.averageMs)} · P95 {formatDuration(metrics?.p95Ms)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="client-debug-summary-card">
                            <h3>Playbook usage</h3>
                            {Object.keys(playbookUsage).length === 0 ? <p className="client-debug-muted">No playbook usage data.</p> : (
                                <div className="client-debug-usage-list">
                                    {Object.entries(playbookUsage).map(([playbook, count]) => (
                                        <div key={playbook}><span>{playbook}</span><strong>{formatNumber(count)}</strong></div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}

function PlaybooksSection({ playbooks, loading, error }) {
    return (
        <section className="client-debug-playbooks" aria-label="Client Debug playbooks">
            <div className="client-debug-section-heading">
                <div>
                    <h2>Playbooks</h2>
                    <p>Read-only collection and analysis guidance.</p>
                </div>
            </div>
            {error && <div className="client-debug-load-error">{error}</div>}
            {loading ? <div className="client-debug-summary-loading">Loading playbooks…</div> : playbooks.length === 0 ? (
                <div className="client-debug-empty">No playbook metadata is available.</div>
            ) : (
                <div className="client-debug-playbook-grid">
                    {playbooks.map((playbook) => {
                        const permitted = playbook.permittedArtifactTypes
                            || playbook.permittedArtifacts
                            || playbook.artifactTypes
                            || playbook.logs;
                        return (
                            <article className="client-debug-playbook-card" key={playbook.name || playbook.displayName}>
                                <h3>{playbook.displayName || playbook.name || 'Unnamed playbook'}</h3>
                                <p>{playbook.description || 'No description provided.'}</p>
                                <dl>
                                    <div><dt>Permitted artifact types</dt><dd>{displayList(permitted)}</dd></div>
                                    <div><dt>Analyses</dt><dd>{displayList(playbook.analyses)}</dd></div>
                                    <div><dt>Collection window</dt><dd>{collectionWindow(playbook)}</dd></div>
                                </dl>
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function ArtifactDetails({ artifact }) {
    const analyzed = Boolean(artifact.analysisId || artifact.analyzed);
    const staged = artifact.staged === true ? 'Staged' : artifact.staged === false ? 'Not staged' : 'Not recorded';
    return (
        <div className="client-debug-artifact">
            <div><span>Logical location</span><strong>{safeLogicalLocation(artifact.uri)}</strong></div>
            <div><span>Artifact type</span><strong>{artifact.artifactType || (artifact.kind === 'file' ? 'Uploaded file' : 'Log')}</strong></div>
            <div><span>Collection time</span><strong>{formatTimestamp(artifact.collectedAt)}</strong></div>
            <div><span>Staging state</span><strong>{staged}</strong></div>
            <div><span>Analyzed state</span><strong>{analyzed ? 'Analyzed' : 'Not analyzed'}</strong></div>
        </div>
    );
}

function JobDetails({ job }) {
    const steps = asList(job.steps);
    const artifacts = asList(job.artifacts);
    const analyses = asList(job.analyses);
    return (
        <div className="client-debug-job-details">
            <div className="client-debug-detail-grid">
                <div><span>Issue time</span><strong>{formatTimestamp(job.issueTime)}</strong></div>
                <div><span>Playbook</span><strong>{job.playbook || '—'}</strong></div>
                <div><span>Requested analyses</span><strong>{displayList(analyses)}</strong></div>
                <div><span>Email delivery</span><strong>{job.email?.sentAt ? `Sent ${formatTimestamp(job.email.sentAt)}` : 'Not recorded'}</strong></div>
            </div>

            {job.statusReason && <div className="client-debug-reason"><span>Status detail</span>{job.statusReason}</div>}
            {job.error?.code && <div className="client-debug-error"><span>{job.error.code}</span>{job.error.message || 'The job reported an error.'}</div>}

            <div className="client-debug-detail-section">
                <h4>Run stages</h4>
                {steps.length === 0 ? <p className="client-debug-muted">No stage updates recorded yet.</p> : (
                    <div className="client-debug-stage-list">
                        {steps.map((step) => (
                            <div className="client-debug-stage" key={`${step.seq}-${step.name}`}>
                                <div className="client-debug-stage-main">
                                    <span className="client-debug-stage-seq">{step.seq}</span>
                                    <strong>{step.name}</strong>
                                    <span className={`client-debug-stage-status ${String(step.status || '').toLowerCase()}`}>{step.status}</span>
                                </div>
                                <div className="client-debug-stage-meta">
                                    {step.detail || '—'}{step.durationMs != null ? ` · ${formatDuration(step.durationMs)}` : ''}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="client-debug-detail-section">
                <h4>Evidence and uploads</h4>
                <p className="client-debug-muted">{artifactSummary(artifacts)} recorded. Analysis output remains in the result email.</p>
                {artifacts.length > 0 && (
                    <div className="client-debug-artifact-list">
                        {artifacts.map((artifact, index) => (
                            <ArtifactDetails artifact={artifact} key={`${artifact.kind || 'artifact'}-${artifact.uri || index}`} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ClientDebugAnalytics({ userInfo }) {
    const [jobs, setJobs] = useState([]);
    const [playbooks, setPlaybooks] = useState([]);
    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [playbooksLoading, setPlaybooksLoading] = useState(true);
    const [error, setError] = useState(null);
    const [analyticsError, setAnalyticsError] = useState(null);
    const [playbooksError, setPlaybooksError] = useState(null);
    const [rangeDays, setRangeDays] = useState(30);
    const [expandedJobId, setExpandedJobId] = useState(null);

    const loadJobs = useCallback(async () => {
        setLoading(true);
        const result = await fetchClientDebugJobs({ userEmail: userInfo });
        setJobs(result.jobs);
        setError(result.errorMessage || null);
        setLoading(false);
    }, [userInfo]);

    const loadAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        const result = await fetchClientDebugAnalytics({ userEmail: userInfo, rangeDays });
        setAnalytics(result.analytics);
        setAnalyticsError(result.errorMessage || null);
        setAnalyticsLoading(false);
    }, [rangeDays, userInfo]);

    const loadPlaybooks = useCallback(async () => {
        setPlaybooksLoading(true);
        const result = await fetchClientDebugPlaybooks({ userEmail: userInfo });
        setPlaybooks(result.playbooks);
        setPlaybooksError(result.errorMessage || null);
        setPlaybooksLoading(false);
    }, [userInfo]);

    const refresh = useCallback(async () => {
        await Promise.all([loadJobs(), loadAnalytics(), loadPlaybooks()]);
    }, [loadAnalytics, loadJobs, loadPlaybooks]);

    useEffect(() => { loadJobs(); }, [loadJobs]);
    useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
    useEffect(() => { loadPlaybooks(); }, [loadPlaybooks]);

    const refreshDisabled = loading || analyticsLoading || playbooksLoading;

    return (
        <div className="client-debug-analytics">
            <div className="client-debug-page-header">
                <div>
                    <h1>Client Debug</h1>
                    <p>Job activity, ownership, status, and execution metadata. RCA and analysis output remain in email.</p>
                </div>
                <button type="button" className="client-debug-refresh" onClick={refresh} disabled={refreshDisabled}>
                    {refreshDisabled ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            <AnalyticsSummary
                analytics={analytics}
                loading={analyticsLoading}
                error={analyticsError}
                rangeDays={rangeDays}
                onRangeChange={setRangeDays}
            />

            <PlaybooksSection playbooks={playbooks} loading={playbooksLoading} error={playbooksError} />

            {error && <div className="client-debug-load-error">{error}</div>}
            {loading && jobs.length === 0 && <div className="client-debug-empty">Loading Client Debug jobs…</div>}
            {!loading && !error && jobs.length === 0 && <div className="client-debug-empty">No Client Debug jobs have been recorded.</div>}

            {jobs.length > 0 && (
                <div className="client-debug-table-wrap">
                    <table className="client-debug-table">
                        <thead>
                            <tr><th>Created</th><th>Job</th><th>Device</th><th>Requested by</th><th>Status</th><th>Playbook</th><th>Email</th><th>Details</th></tr>
                        </thead>
                        <tbody>
                            {jobs.map((job) => {
                                const expanded = expandedJobId === job.jobId;
                                return (
                                    <React.Fragment key={job.jobId}>
                                        <tr className={expanded ? 'expanded' : ''}>
                                            <td>{formatTimestamp(job.createdAt)}</td>
                                            <td><code title={job.jobId}>{String(job.jobId).slice(0, 8)}</code></td>
                                            <td>{job.deviceId || '—'}</td>
                                            <td>{job.requestedBy || 'Automated trigger'}</td>
                                            <td><span className={`client-debug-status ${String(job.status || '').toLowerCase()}`}>{statusLabel(job.status)}</span></td>
                                            <td>{job.playbook || '—'}</td>
                                            <td>{job.email?.sentAt ? 'Sent' : '—'}</td>
                                            <td><button type="button" className="client-debug-details-button" onClick={() => setExpandedJobId(expanded ? null : job.jobId)}>{expanded ? 'Hide' : 'View'}</button></td>
                                        </tr>
                                        {expanded && <tr className="client-debug-expanded-row"><td colSpan="8"><JobDetails job={job} /></td></tr>}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default ClientDebugAnalytics;
